import { getBrowserBrand } from 'functions/browserPermissions';

export async function getCameras() {
  const cameras = (await navigator.mediaDevices.enumerateDevices()).filter(
    (device) => device.kind === 'videoinput'
  );
  const backCamera = cameras.find(
    (camera) =>
      camera.label.toLowerCase().includes('facing back') ||
      camera.label.toLowerCase().includes('back camera')
  );
  const frontCamera = cameras.find(
    (camera) =>
      camera.label.toLowerCase().includes('facing front') ||
      camera.label.toLowerCase().includes('front camera')
  );
  return { backCamera, frontCamera, cameras };
}

export async function initVideoStream(
  isBackCamera: boolean,
  elementId: string
) {
  const videoStream = await navigator.mediaDevices.getUserMedia({
    audio: false,
    video: {
      facingMode: isBackCamera ? ['environment'] : ['user'],
      focusMode: 'continuous', //'continuous', //'single-shot', //'manual',
      with: { ideal: 3472 },
      height: { ideal: 3472 },
      zoom: 1.6,
    } as any,
  });

  const track = videoStream.getVideoTracks()[0];

  const video = document.getElementById(elementId) as any;
  if (video) {
    video.srcObject = videoStream;
    video.play();
  }

  return {
    videoStream,
    track,
    videoPlayer: video,
  };
}

export async function getVideoCapabilities(track: MediaStreamTrack) {
  const browser = getBrowserBrand();

  if (browser === 'firefox') {
    return await getCapabilitiesForFirefox(track);
  }

  const capabilities = track.getCapabilities();

  const imageWidth = {
    max: capabilities.width?.max || 0,
    min: capabilities.width?.min || 0,
  };
  const imageHeight = {
    max: capabilities.height?.max || 0,
    min: capabilities.height?.min || 0,
  };

  return {
    imageWidth,
    imageHeight,
    isTorchSupported: (capabilities as any).torch,
  };
}

const getCapabilitiesForFirefox = async (track: MediaStreamTrack) => {
  const capabilities = track.getSettings();

  const imageWidth = {
    max: capabilities.width || 0,
    min: capabilities.width || 0,
  };
  const imageHeight = {
    max: capabilities.height || 0,
    min: capabilities.height || 0,
  };

  const info = await navigator.mediaCapabilities.decodingInfo({
    type: 'media-source',
    video: {
      contentType: 'video/mp4; codecs="avc1.4d002a"',
      bitrate: 60,
      framerate: 60,
      height: capabilities.height || 0,
      width: capabilities.width || 0,
    },
  });

  return {
    imageWidth,
    imageHeight,
    isTorchSupported: info.supported && info.powerEfficient,
  };
};

export function stopCamera(videoStream: MediaStream) {
  if (videoStream) {
    const tracks = videoStream.getTracks();
    tracks.forEach(function (track: any) {
      track.stop();
    });
  }
}

export async function getViewPortImageWithCanvas(
  canvas: HTMLCanvasElement,
  viewPortWidth: number,
  viewPortHeight: number
) {
  const ratio = viewPortWidth / viewPortHeight;
  const newWidth = canvas.width;
  const newHeight = newWidth / ratio;
  const newX = (canvas.width - newWidth) / 2;
  const newY = (canvas.height - newHeight) / 2;

  const croppedImage = await cropImageWithCanvas(
    canvas,
    newX + newHeight < canvas.width ? newX : 0,
    newY + newHeight < canvas.height ? newY : 0,
    newWidth < canvas.width ? newWidth : canvas.width,
    newHeight < canvas.height ? newHeight : canvas.height
  );

  return croppedImage;
}

export async function getViewPortImage(
  imagePath: string,
  viewPortWidth: number,
  viewPortHeight: number
) {
  const originalImage = new Image();
  originalImage.src = imagePath;
  await originalImage.decode();

  const ratio = viewPortWidth / viewPortHeight;
  const newWidth = originalImage.width;
  const newHeight = newWidth / ratio;
  const newX = (originalImage.width - newWidth) / 2;
  const newY = (originalImage.height - newHeight) / 2;

  const croppedImage = await cropImage(
    originalImage,
    newX,
    newY,
    newWidth,
    newHeight
  );

  return croppedImage;
}

export function cropImage(
  originalImage: HTMLImageElement,
  newX: number,
  newY: number,
  newWidth: number,
  newHeight?: number
): string {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const originalWidth = originalImage.naturalWidth;
  const originalHeight = originalImage.naturalHeight;
  const aspectRatio = originalWidth / originalHeight;
  if (newHeight === undefined) {
    newHeight = newWidth / aspectRatio;
  }
  canvas.width = newWidth;
  canvas.height = newHeight;

  ctx!.drawImage(
    originalImage,
    newX,
    newY,
    newWidth,
    newHeight,
    0,
    0,
    newWidth,
    newHeight
  );

  const croppedImage = canvas.toDataURL('image/jpeg', 0.9);
  return croppedImage;
}

export function cropImageWithCanvas(
  originalCanvas: HTMLCanvasElement,
  newX: number,
  newY: number,
  newWidth: number,
  newHeight?: number
): string {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const originalWidth = originalCanvas.width;
  const originalHeight = originalCanvas.height;
  const aspectRatio = originalWidth / originalHeight;
  if (newHeight === undefined) {
    newHeight = newWidth / aspectRatio;
  }
  canvas.width = newWidth;
  canvas.height = newHeight;

  ctx!.drawImage(
    originalCanvas,
    newX,
    newY,
    newWidth,
    newHeight,
    0,
    0,
    newWidth,
    newHeight
  );

  const croppedImage = canvas.toDataURL('image/jpeg', 0.9);
  return croppedImage;
}

export async function takeAPhoto(
  video: HTMLVideoElement,
  width: number,
  height: number,
  withAddionalFrame?: boolean,
  calculateBlueScore?: boolean
) {
  if (!video) return { image: '', focusMeasure: undefined };

  const addionalFrameSize = 0.2;
  const canvas = document.createElement('canvas')! as HTMLCanvasElement;
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d')!;
  context.drawImage(video, 0, 0, width, height);

  const addionalOffsetHeightFrame =
    withAddionalFrame && video.offsetHeight * (1 + addionalFrameSize) <= height
      ? video.offsetHeight * addionalFrameSize
      : 0;

  const croppedImage = await getViewPortImageWithCanvas(
    canvas,
    video.offsetWidth,
    video.offsetHeight + addionalOffsetHeightFrame
  );

  canvas.remove();

  const focusQuality = calculateBlueScore ? getFocusQuality(video) : 0;
  const { isBlankImage, isWhiteImage } = calculateBlueScore
    ? getMeanIntensity(video)
    : { isBlankImage: false, isWhiteImage: false };

  return {
    image: croppedImage,
    focusQuality,
    isBlankImage,
    isWhiteImage,
  };
}

export async function takeManyPhotos(
  video: HTMLVideoElement,
  width: number,
  height: number,
  imagesCount: number,
  calculateBlurScore?: boolean,
  timeBetweenImages?: number
) {
  if (!video) return [];

  const canvas = document.createElement('canvas')! as HTMLCanvasElement;
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d')!;

  const generatesImages = (): Promise<any[]> => {
    const images: any[] = [];

    return new Promise<any[]>((resolve) => {
      const generateImage = () => {
        setTimeout(() => {
          (async () => {
            context.drawImage(video, 0, 0, width, height);
            const croppedImage = await getViewPortImageWithCanvas(
              canvas,
              video.offsetWidth,
              video.offsetHeight
            );
            const focusQuality = calculateBlurScore
              ? getFocusQuality(video)
              : 0;
            console.log(`focusQuality image ${images.length} >>`, focusQuality);
            images.push({
              image: croppedImage,
              focusQuality,
            });
            if (images.length < imagesCount) {
              generateImage();
            } else {
              resolve(images);
            }
          })();
        }, timeBetweenImages || 250);
      };
      generateImage();
    });
  };

  const images = await generatesImages();

  canvas.remove();

  return images;
}

export async function stopCameraStreams() {
  const media = await navigator.mediaDevices.getUserMedia({ video: true });
  media.getTracks().forEach((track) => track.stop());
}

const mean = (array: number[] = []) =>
  array.reduce((acc, x) => acc + x, 0) / array.length;

function variance(array: number[]) {
  const m = mean(array);
  return array.reduce((acc, el) => acc + Math.pow(el - m, 2), 0) / array.length;
}

export declare const cv: any;

export function getFocusQuality(video: HTMLVideoElement) {
  const src = new cv.Mat(video.height, video.width, cv.CV_8UC4);
  const gray_src = new cv.Mat(video.height, video.width, cv.CV_8UC1);
  const lap_gray = new cv.Mat(video.height, video.width, cv.CV_8UC1);
  const cap = new cv.VideoCapture(video);

  // start processing.
  cap.read(src); // cap -> src

  // process src -> dst
  cv.cvtColor(src, gray_src, cv.COLOR_RGBA2GRAY);
  cv.Laplacian(gray_src, lap_gray, -1);
  const focusQuality = Math.floor(variance(lap_gray.data));

  return focusQuality;
}

export function getFocusQualityAndImage(video: HTMLVideoElement) {
  const src = new cv.Mat(video.height, video.width, cv.CV_8UC4);
  const gray_src = new cv.Mat(video.height, video.width, cv.CV_8UC1);
  const lap_gray = new cv.Mat(video.height, video.width, cv.CV_8UC1);
  const cap = new cv.VideoCapture(video);

  // start processing.
  cap.read(src); // cap -> src

  const dst = new cv.Mat(video.height, video.width, cv.CV_8UC4);
  src.copyTo(dst);

  // process src -> dst
  cv.cvtColor(src, gray_src, cv.COLOR_RGBA2GRAY);
  cv.Laplacian(gray_src, lap_gray, -1);
  const focusQuality = Math.floor(variance(lap_gray.data));

  return { focusQuality, image: dst };
}

export function getFocusQualityAndImageHD(
  video: HTMLVideoElement,
  width: number,
  height: number
) {
  const src = new cv.Mat(video.height, video.width, cv.CV_8UC4);
  const gray_src = new cv.Mat(video.height, video.width, cv.CV_8UC1);
  const lap_gray = new cv.Mat(video.height, video.width, cv.CV_8UC1);
  const cap = new cv.VideoCapture(video);

  // start processing.
  cap.read(src); // cap -> src

  const dst = new cv.Mat(video.height, video.width, cv.CV_8UC4);
  src.copyTo(dst);

  // process src -> dst
  cv.cvtColor(src, gray_src, cv.COLOR_RGBA2GRAY);
  cv.Laplacian(gray_src, lap_gray, -1);
  const focusQuality = Math.floor(variance(lap_gray.data));

  const canvas = document.createElement('canvas')! as HTMLCanvasElement;
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d')!;
  context.drawImage(video, 0, 0, width, height);

  const { isBlankImage, isWhiteImage } = getMeanIntensity(video);

  return { focusQuality, image: dst, canvas, isBlankImage, isWhiteImage };
}

export function getMeanIntensity(video: HTMLVideoElement) {
  const src = new cv.Mat(video.height, video.width, cv.CV_8UC4);
  const gray = new cv.Mat(video.height, video.width, cv.CV_8UC1);
  const cap = new cv.VideoCapture(video);

  // Capture frame
  cap.read(src);

  // Convert to grayscale
  cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);

  // Calculate mean intensity
  const meanScalar = cv.mean(gray);
  const meanIntensity = meanScalar[0]; // First channel for grayscale

  const DARK_THRESHOLD = 10; // Adjust if needed (0 is pure black)
  const WHITE_THRESHOLD = 240; // Adjust if needed (255 is pure white)
  // Cleanup
  src.delete();
  gray.delete();

  return {
    meanIntensity,
    isBlankImage: meanIntensity < DARK_THRESHOLD,
    isWhiteImage: meanIntensity > WHITE_THRESHOLD,
    isOK: meanIntensity > DARK_THRESHOLD && meanIntensity < WHITE_THRESHOLD,
  };
}

export function getFocusQualityFromBase64Image(
  base64Image: string
): Promise<number> {
  return new Promise((resolve, reject) => {
    // Decode the base64 string into an image
    const img = new Image();
    img.onload = function () {
      try {
        // Create a canvas element to draw the image onto
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0);

        // Load the image
        const cvImg = cv.imread(canvas);

        // Convert to grayscale
        const gray = new cv.Mat();
        cv.cvtColor(cvImg, gray, cv.COLOR_BGR2GRAY);

        // Calculate the Laplacian
        const laplacian = new cv.Mat();
        cv.Laplacian(gray, laplacian, cv.CV_64F);

        // Calculate the mean and standard deviation of the Laplacian
        const mean = new cv.Mat();
        const stdDev = new cv.Mat();
        cv.meanStdDev(laplacian, mean, stdDev);

        // Compute a blur score based on the mean and standard deviation
        const blurScore = Math.pow(stdDev.data64F[0], 2) / mean.data64F[0];

        // Check if the image is blurry or not
        if (blurScore < 100) {
          console.log('Image is blurry');
        } else {
          console.log('Image is not blurry');
        }

        // Cleanup
        cvImg.delete();
        gray.delete();
        laplacian.delete();
        mean.delete();
        stdDev.delete();
        resolve(blurScore as number);
      } catch (error) {
        reject(error);
      }
    };
    //img.src = `data:image/png;base64,${base64Image}`;
    img.src = base64Image;
  });
}

export function getFocusQualityFromCanvas(canvas: HTMLCanvasElement): number {
  // Load the image
  const cvImg = cv.imread(canvas);

  // Convert to grayscale
  const gray = new cv.Mat();
  cv.cvtColor(cvImg, gray, cv.COLOR_BGR2GRAY);

  // Calculate the Laplacian
  const laplacian = new cv.Mat();
  cv.Laplacian(gray, laplacian, cv.CV_64F);

  // Calculate the mean and standard deviation of the Laplacian
  const mean = new cv.Mat();
  const stdDev = new cv.Mat();
  cv.meanStdDev(laplacian, mean, stdDev);

  // Compute a blur score based on the mean and standard deviation
  const blurScore = Math.pow(stdDev.data64F[0], 2) / mean.data64F[0];

  // Cleanup
  cvImg.delete();
  gray.delete();
  laplacian.delete();
  mean.delete();
  stdDev.delete();

  return blurScore;
}

export async function takeTheBestPhotoWithOpenCV(
  video: HTMLVideoElement,
  width: number,
  height: number,
  frames = 100,
  photosCount = 5
): Promise<{
  image: string;
  focusQuality: number;
  isBlankImage?: boolean;
  isWhiteImage?: boolean;
}> {
  return new Promise((resolve) => {
    const images: {
      image: any;
      focusQuality: number;
      canvas: HTMLCanvasElement;
      isBlankImage?: boolean;
      isWhiteImage?: boolean;
    }[] = [];

    const captureImageFromVideo = () => {
      const image = getFocusQualityAndImageHD(video, width, height);
      images.push(image);

      if (images.length < photosCount) {
        setTimeout(captureImageFromVideo, frames);
      } else {
        const bestImage = images.sort(
          (a, b) => b.focusQuality - a.focusQuality
        )[0];

        (async () => {
          const croppedImage = await getViewPortImageWithCanvas(
            bestImage.canvas,
            video.offsetWidth,
            video.offsetHeight
          );

          resolve({
            image: croppedImage,
            focusQuality: bestImage.focusQuality,
            isBlankImage: bestImage.isBlankImage,
            isWhiteImage: bestImage.isWhiteImage,
          });
        })();
      }
    };

    captureImageFromVideo();
  });
}
